import * as React from 'react';
import { cn } from '@/shared/lib/cn';
import type { TocHeading } from '../lib/extractTocHeadings';
import { useActiveHeading } from '../model/useActiveHeading';
import styles from './DocToc.module.css';

type DocTocProps = {
  markdown: string;
};

const DEPTH_CLASSES: Record<number, string | undefined> = {
  1: styles.depth1,
  2: styles.depth2,
  3: styles.depth3,
  4: styles.depth4,
  5: styles.depth5,
  6: styles.depth6,
};

const NO_HEADINGS: TocHeading[] = [];

export function DocToc({ markdown }: DocTocProps) {
  const [loaded, setLoaded] = React.useState<TocHeading[] | null>(null);
  React.useEffect(() => {
    let alive = true;
    void import('../lib/extractTocHeadings').then(({ extractTocHeadings }) => {
      if (alive) setLoaded(extractTocHeadings(markdown));
    });
    return () => {
      alive = false;
    };
  }, [markdown]);
  const headings = loaded ?? NO_HEADINGS;
  const ids = React.useMemo(() => headings.map((h) => h.id), [headings]);
  const activeId = useActiveHeading(ids);
  const listRef = React.useRef<HTMLUListElement>(null);
  const [indicatorStyle, setIndicatorStyle] = React.useState<React.CSSProperties>({
    opacity: 0,
  });

  const handleClick = React.useCallback((e: React.MouseEvent<HTMLUListElement>) => {
    const target = e.target as HTMLElement;
    const link = target.closest<HTMLAnchorElement>('a[data-heading-id]');
    if (!link) return;
    e.preventDefault();
    const id = link.dataset.headingId;
    if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const INSET = 3;

    const measure = () => {
      if (!activeId) {
        setIndicatorStyle({ opacity: 0 });
        return;
      }
      const link = list.querySelector<HTMLElement>(`a[data-heading-id="${activeId}"]`);
      const item = link?.closest<HTMLLIElement>('li');
      if (!item) {
        setIndicatorStyle({ opacity: 0 });
        return;
      }
      setIndicatorStyle({
        opacity: 1,
        transform: `translateY(${item.offsetTop + INSET}px)`,
        height: `${item.offsetHeight - INSET * 2}px`,
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [activeId, headings.length]);

  React.useEffect(() => {
    if (!activeId) return;
    const list = listRef.current;
    if (!list) return;
    if (list.scrollHeight <= list.clientHeight) return;
    const el = list.querySelector<HTMLElement>(`a[data-heading-id="${activeId}"]`);
    if (!el) return;
    const listRect = list.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    if (elRect.top >= listRect.top && elRect.bottom <= listRect.bottom) return;
    const target =
      elRect.top - listRect.top + list.scrollTop - list.clientHeight / 2 + elRect.height / 2;
    list.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }, [activeId]);

  return (
    <nav className={styles.toc} aria-label='Содержание'>
      <p className={styles.title}>На этой странице</p>
      {loaded === null ? null : headings.length === 0 ? (
        <p className={styles.empty}>Нет заголовков</p>
      ) : (
        <ul ref={listRef} className={styles.list} onClick={handleClick}>
          <li aria-hidden role='presentation' className={styles.indicator} style={indicatorStyle} />
          {headings.map((h) => (
            <li key={h.id} className={cn(styles.item, DEPTH_CLASSES[h.depth])}>
              <a
                href={`#${h.id}`}
                data-heading-id={h.id}
                className={cn(styles.link, activeId === h.id && styles.linkActive)}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}
